// CustomerOrderList.jsx
// (Moved from my-app)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import { FiCheckCircle, FiXCircle, FiClock, FiRefreshCw, FiArrowLeft, FiShoppingCart } from 'react-icons/fi';
import toast, { Toaster } from 'react-hot-toast';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import '../styles/CustomerOrderList.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';


export default function CustomerOrderList() {
  const { userId } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [refundedItems, setRefundedItems] = useState({});
  const [refundedOrders, setRefundedOrders] = useState({});
  const [userInfo, setUserInfo] = useState(null);

  // Memoize fetchOrders to avoid react-hooks/exhaustive-deps warning
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data: user } = await supabase.from("users").select("*").eq("id", userId).maybeSingle();
      if (user) setUserInfo(user);

      const { data: orderRows, error: ordersError } = await supabase
        .from("orders").select("*, order_items(*)").eq("user_id", userId);
      if (ordersError) throw ordersError;

      const orderIds = (orderRows || []).map(o => o.id);
      const [{ data: ticketRows }, { data: kkRows }] = orderIds.length
        ? await Promise.all([
            supabase.from("tickets").select("*").in("order_id", orderIds),
            supabase.from("klippekort").select("*").in("order_id", orderIds),
          ])
        : [{ data: [] }, { data: [] }];
      const kkIds = (kkRows || []).map(k => k.id);
      const { data: kkRedemptionRows } = kkIds.length
        ? await supabase.from("klippekort_redemptions").select("*").in("klippekort_id", kkIds)
        : { data: [] };

      const fetchedOrders = (orderRows || [])
        .filter(order => !!order.order_reference)
        .map(order => {
          const productsForOrder = [
            ...(ticketRows || []).filter(t => t.order_id === order.id).map(t => ({
              id: t.id, name: t.name, price: t.price, quantity: t.quantity,
              category: t.category, stampAmounts: null, refunded: t.refunded, table: 'tickets',
            })),
            ...(kkRows || []).filter(k => k.order_id === order.id).map(k => ({
              id: k.id, name: k.name, price: null, quantity: 1,
              category: 'klippekort', stampAmounts: k.stamp_amounts, refunded: k.refunded, table: 'klippekort',
            })),
            ...(kkRedemptionRows || []).filter(r => r.klippekort_id && (kkRows || []).some(k => k.id === r.klippekort_id && k.order_id === order.id)).map(r => ({
              id: r.id, name: r.name, price: null, quantity: 1,
              category: 'klippekort', stampAmounts: null, refunded: r.refunded, table: 'klippekort_redemptions',
            })),
          ];

          return {
            id: order.id,
            orderReference: order.order_reference,
            buyerName: order.buyer_name,
            phoneNumber: order.phone_number,
            datePurchased: order.date_purchased,
            totalPrice: order.total_price,
            captureStatus: order.capture_status,
            paymentMethod: order.payment_method,
            capturedAt: order.captured_at,
            capturedAmount: order.captured_amount,
            discountCode: order.discount_code,
            discountAmount: order.discount_amount,
            fullyRefunded: order.fully_refunded,
            refundedItems: order.refunded_items,
            vippsCaptureResponse: order.vipps_capture_response,
            items: (order.order_items || []).map(i => ({
              name: i.name, price: i.price, quantity: i.quantity, category: i.category, stampAmounts: null,
            })),
            products: productsForOrder,
          };
        });

      // Sort newest first
      fetchedOrders.sort((a, b) => new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0));
      setOrders(fetchedOrders);

      const refundedItemsFromDB = {};
      const refundedOrdersFromDB = {};
      fetchedOrders.forEach(order => {
        if (order.refundedItems) {
          Object.assign(refundedItemsFromDB, order.refundedItems);
        }
        if (order.fullyRefunded) {
          refundedOrdersFromDB[order.orderReference] = true;
        }
      });
      setRefundedItems(refundedItemsFromDB);
      setRefundedOrders(refundedOrdersFromDB);
    } catch (err) {
      setError("Kunne ikke hente ordre: " + err.message);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Remove localStorage fallback: always use Firestore as source of truth for refund state
  // This ensures the UI always reflects the latest backend data after refund/capture

  const toggleDropdown = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  // Helper function to check if order is captured (based on database only)
  const isOrderCaptured = (order) => {
    return order.captureStatus === "CAPTURED";
  };

  // Helper function to get detailed order status
  const getOrderStatus = (order) => {
    const isFullyRefunded = refundedOrders[order.orderReference];
    const isCaptured = order.captureStatus === 'CAPTURED';
    
    // Use Vipps data for accurate refund status, falling back to our own
    // refunded_items tracking - our capture-payment endpoint doesn't store
    // Vipps' full aggregate breakdown, so this fallback is the normal case.
    const vippsData = order.vippsCaptureResponse?.aggregate;
    const capturedAmount = vippsData ? (vippsData.capturedAmount?.value || 0) / 100 : (order.capturedAmount || 0);
    let refundedAmount = vippsData ? (vippsData.refundedAmount?.value || 0) / 100 : 0;
    if (!vippsData) {
      (order.items || order.products || []).forEach(item => {
        const key = `${order.orderReference}_${item.name}`;
        const refundedQty = refundedItems[key] || 0;
        refundedAmount += refundedQty * (item.price || 0);
      });
    }
    
    // Check for full refund based on amounts
    if (isFullyRefunded || (capturedAmount > 0 && refundedAmount >= capturedAmount)) {
      return 'fully-refunded';
    }
    
    // Check for partial refunds
    if (refundedAmount > 0) {
      return 'partially-refunded';
    }
    
    // Check if captured
    if (isCaptured || capturedAmount > 0) {
      return 'captured-paid';
    }
    
    return 'pending-authorized';
  };

  // Helper function to get status display info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'captured-paid':
        return { label: 'Betalt og innkrevd', color: '#27ae60', icon: FiCheckCircle, bg: '#d4edda' };
      case 'fully-refunded':
        return { label: 'Helt refundert', color: '#e74c3c', icon: FiXCircle, bg: '#f8d7da' };
      case 'partially-refunded':
        return { label: 'Delvis refundert', color: '#fd7e14', icon: FiXCircle, bg: '#fff3cd' };
      case 'pending-authorized':
      default:
        return { label: 'Autorisert (Ventende)', color: '#6f42c1', icon: FiClock, bg: '#e2e3f5' };
    }
  };

  // Helper function to calculate refund amounts using both custom tracking and Vipps data
  const getRefundInfo = (order) => {
    const orderItems = order.items || order.products || [];
    let totalRefundedAmount = 0;
    let totalRefundedItems = 0;
    let totalItems = 0;
    
    // Calculate from custom refundedItems tracking for item-level details
    orderItems.forEach(item => {
      const key = `${order.orderReference}_${item.name}`;
      const refundedQty = refundedItems[key] || 0;
      totalRefundedAmount += refundedQty * (item.price || 0);
      totalRefundedItems += refundedQty;
      totalItems += item.quantity || 1;
    });
    
    // Use Vipps response data for accurate refunded amount if available
    const vippsRefundedAmount = order.vippsCaptureResponse?.aggregate?.refundedAmount?.value || 0;
    const actualRefundedAmount = vippsRefundedAmount > 0 ? vippsRefundedAmount / 100 : totalRefundedAmount;
    
    return {
      totalRefundedAmount: actualRefundedAmount,
      totalRefundedItems,
      totalItems,
      refundPercentage: totalItems > 0 ? Math.round((totalRefundedItems / totalItems) * 100) : 0,
      vippsRefundedAmount: vippsRefundedAmount / 100 // Store in NOK for display
    };
  };

  // Helper function to get payment amounts from Vipps data
  const getPaymentAmounts = (order) => {
    const vippsData = order.vippsCaptureResponse?.aggregate;
    if (!vippsData) {
      const capturedAmount = isOrderCaptured(order) ? (order.capturedAmount || order.totalPrice || 0) : 0;
      // Our own capture-payment endpoint never stores Vipps' full aggregate
      // breakdown (just {success, pspReference, capturedAmount}), so this
      // branch is the normal case here, not a rare fallback - falls back to
      // the same refunded_items tracking getRefundInfo() already uses, or
      // refunded_items would silently never be reflected in these totals.
      let refundedAmount = 0;
      (order.items || order.products || []).forEach(item => {
        const key = `${order.orderReference}_${item.name}`;
        const refundedQty = refundedItems[key] || 0;
        refundedAmount += refundedQty * (item.price || 0);
      });
      return {
        authorizedAmount: order.totalPrice || 0,
        capturedAmount,
        refundedAmount,
        availableToRefund: Math.max(0, capturedAmount - refundedAmount)
      };
    }

    const authorizedAmount = (vippsData.authorizedAmount?.value || 0) / 100;
    const capturedAmount = (vippsData.capturedAmount?.value || 0) / 100;
    const refundedAmount = (vippsData.refundedAmount?.value || 0) / 100;
    const availableToRefund = capturedAmount - refundedAmount;

    return {
      authorizedAmount,
      capturedAmount,
      refundedAmount,
      availableToRefund: Math.max(0, availableToRefund)
    };
  };

  // Find one not-yet-refunded ticket doc (from the real myTickets/myKlippekort/myPasses
  // subcollections) matching a summary item's name, so a "Refund 1x" click can flag that
  // exact ticket instead of only bumping a name-keyed count that can't tell which of
  // several same-named tickets was refunded.
  function findRefundableTicketDocRef(order, itemName) {
    const match = (order.products || []).find(p => p.name === itemName && p.refunded !== true);
    return match ? match.id : null;
  }

  async function handleRefund(orderReference, item, refundQty, pricePerUnit, ticketDocRef) {
    try {
      const refundReason = item
        ? `Refunder vare: ${item.name} x${refundQty}`
        : "Full ordrerefundering";
      const amountInMinorUnits = Math.round(pricePerUnit * refundQty * 100);
      // Prepare payload for backend
      const payload = {
        reference: orderReference,
        amountValue: amountInMinorUnits,
        refundReason: refundReason,
        userId,
      };
      if (item) {
        payload.itemName = item.name;
        payload.refundQty = refundQty;
      }
      if (ticketDocRef) {
        payload.ticketDocRef = ticketDocRef;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/refund-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      const backendResult = await response.json();
      if (!response.ok) {
        console.error("Refund backend error:", backendResult);
        toast.error(`Refund failed: ${backendResult.error || backendResult.detail || JSON.stringify(backendResult)}`, { duration: 4000, position: 'top-center' });
        return;
      }
      toast.success("Refundering vellykket", { duration: 2500, position: 'top-center' });
      setOpenIndex(null); // close any open dropdowns to force UI refresh
      await fetchOrders(); // always use backend/Firestore as source of truth
    } catch (error) {
      toast.error("Refunderingsfeil: " + error.message, { duration: 4000, position: 'top-center' });
      console.error("Refund error:", error);
    }
  }

  async function handleCapture(orderReference, totalPrice) {
    try {
      const amountInMinorUnits = Math.round(totalPrice * 100);
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/capture-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          reference: orderReference,
          amountValue: amountInMinorUnits,
          userId,
        }),
      });
      const backendResult = await response.json();
      if (!response.ok) {
        console.error("Capture backend error:", backendResult);
        alert(`Capture failed: ${backendResult.error || backendResult.detail || JSON.stringify(backendResult)}`);
        return;
      }
      alert("Betaling innkrevd vellykket");
      setOpenIndex(null);
      await fetchOrders();
    } catch (error) {
      alert("Innkrevingsfeil: " + error.message);
      console.error("Capture error:", error);
    }
  }

  if (loading) {
    return (
      <div className="customer-orders-container">
        <div className="loading-container">
          <FiRefreshCw className="loading-spinner" />
          <p>Laster kundebestillinger...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-orders-container">
        <div className="error-container">
          <p>Feil: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster />
      <AdminDashboardNav />
      <div className="customer-orders-container">
      {/* Header */}
      <div className="customer-orders-header">
        <div>
          <h1 className="customer-orders-title">
            <FiShoppingCart style={{ marginRight: '12px', verticalAlign: 'middle' }} />
            Kundebestillinger
          </h1>
          {userInfo && (
            <p className="customer-info">
              {userInfo.name || userInfo.email || 'Ukjent bruker'}
            </p>
          )}
          <p className="order-count">{orders.length} bestillinger funnet</p>
        </div>
        <Link to="/users" className="customer-orders-backlink">
          <FiArrowLeft />
          Tilbake til brukere
        </Link>
      </div>
      {orders.length === 0 ? (
        <div className="no-orders">
          <p>Ingen bestillinger funnet for denne kunden.</p>
        </div>
      ) : (
        <div className="orders-grid">
          {orders.map((order, idx) => {
            const status = getOrderStatus(order);
            const statusInfo = getStatusInfo(status);
            const StatusIcon = statusInfo.icon;
            const refundInfo = getRefundInfo(order);
            const paymentAmounts = getPaymentAmounts(order);
            return (
              <div key={order.id || idx} className="order-card">
                <div className="order-header">
                  <div className="order-reference">
                    <span className="order-id">OR-{(order.orderReference || '').substring(0, 8).padEnd(8, '0')}</span>
                    <div className="order-date">
                      {order.datePurchased
                        ? new Date(order.datePurchased).toLocaleDateString('no-NO', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : "Ukjent dato"}
                    </div>
                    <div className="status-info">
                      <span className={`status-badge ${status}`} style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}>
                        <StatusIcon />
                        {statusInfo.label}
                      </span>
                      <span className="status-badge" style={{ backgroundColor: '#e2e8f0', color: '#475569' }}>
                        {order.paymentMethod === 'CARD' ? 'Kort' : 'Vipps'}
                      </span>
                      {paymentAmounts.refundedAmount > 0 && (
                        <span className="refund-info">
                          {refundInfo.refundPercentage}% refundert ({paymentAmounts.refundedAmount.toFixed(2)} NOK)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="expand-btn"
                    onClick={() => toggleDropdown(idx)}
                    aria-expanded={openIndex === idx}
                  >
                    {openIndex === idx ? "▲" : "▼"}
                  </button>
                </div>
                {openIndex === idx && (
                  <div className="order-content">
                    {/* Payment Summary */}
                    <div className="payment-summary">
                      <h4>Betalingssammendrag</h4>
                      <div className="payment-details">
                        <div className="payment-detail-item">
                          <span className="payment-label">Autorisert beløp:</span>
                          <span className="payment-value">{paymentAmounts.authorizedAmount.toFixed(2)} NOK</span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-label">Innkrevd beløp:</span>
                          <span className="payment-value captured">
                            {paymentAmounts.capturedAmount.toFixed(2)} NOK
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-label">Refundert beløp:</span>
                          <span className="payment-value refunded">
                            {paymentAmounts.refundedAmount.toFixed(2)} NOK
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-label">Tilgjengelig for refusjon:</span>
                          <span className="payment-value available">
                            {paymentAmounts.availableToRefund.toFixed(2)} NOK
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-label">Netto beløp:</span>
                          <span className="payment-value net">
                            {(paymentAmounts.capturedAmount - paymentAmounts.refundedAmount).toFixed(2)} NOK
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-label">Ordrestatus:</span>
                          <span className={`payment-status ${status}`}>
                            <StatusIcon />
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      
                      {/* Order Items Summary */}
                      <div className="order-items-summary">
                        <h4>Ordrevarer</h4>
                        <div className="items-list">
                          {(order.items || order.products || []).map((item, i) => {
                            const key = `${order.orderReference}_${item.name}`;
                            const refundedQty = refundedItems[key] || 0;
                            const totalItemPrice = (item.price || 0) * (item.quantity || 1);
                            const refundedAmount = refundedQty * (item.price || 0);
                            
                            return (
                              <div key={i} className="summary-item" style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '14px'
                              }}>
                                <div className="item-details" style={{ flex: 1 }}>
                                  <div className="item-name" style={{ fontWeight: '600', marginBottom: '2px' }}>
                                    {item.quantity}x {item.name}
                                  </div>
                                  <div className="item-meta" style={{ fontSize: '12px', color: '#666' }}>
                                    {item.price} NOK per stk
                                    {item.category === "klippekort" && item.stampAmounts && (
                                      <span style={{ color: '#7a5af5', marginLeft: '8px' }}>
                                        ({item.stampAmounts} klipp)
                                      </span>
                                    )}
                                    {item.category && (
                                      <span style={{ marginLeft: '8px', textTransform: 'capitalize', background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>
                                        {item.category}
                                      </span>
                                    )}
                                  </div>
                                  {refundedQty > 0 && (
                                    <div className="item-refunded" style={{ color: '#e74c3c', fontSize: '12px', marginTop: '2px' }}>
                                      ⚠️ Refundert: {refundedQty}x
                                    </div>
                                  )}
                                </div>
                                <div className="item-amounts" style={{ textAlign: 'right' }}>
                                  <div className="item-total" style={{ fontWeight: '600', fontSize: '14px' }}>
                                    {totalItemPrice.toFixed(2)} NOK
                                  </div>
                                  {refundedAmount > 0 && (
                                    <div className="item-refund" style={{ color: '#e74c3c', fontSize: '12px' }}>
                                      -{refundedAmount.toFixed(2)} NOK refundert
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {order.discountAmount > 0 && (
                            <div className="summary-item" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px',
                              color: '#e74c3c',
                            }}>
                              <div style={{ fontWeight: '600' }}>
                                🏷 Rabattkode «{order.discountCode}»
                              </div>
                              <div style={{ fontWeight: '700' }}>-{order.discountAmount} NOK</div>
                            </div>
                          )}
                        </div>
                        <div className="items-total" style={{ 
                          marginTop: '12px', 
                          paddingTop: '8px', 
                          borderTop: '2px solid #ddd',
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          <span>Total betalt:</span>
                          <span>{Math.max(0, (order.totalPrice || 0) - paymentAmounts.refundedAmount).toFixed(2)} NOK</span>
                        </div>
                      </div>
                    </div>
                    {/* Order Meta Information */}
                    <div className="order-meta">
                      <div className="order-meta-item">
                        <span className="order-meta-label">Kundenavn</span>
                        <span className="order-meta-value">{order.buyerName || "Ukjent"}</span>
                      </div>
                      <div className="order-meta-item">
                        <span className="order-meta-label">Telefonnummer</span>
                        <span className="order-meta-value">{order.phoneNumber || "Ukjent"}</span>
                      </div>
                      <div className="order-meta-item">
                        <span className="order-meta-label">Kjøpsdato</span>
                        <span className="order-meta-value">
                          {order.datePurchased
                            ? new Date(order.datePurchased).toLocaleString('no-NO')
                            : "Ukjent"}
                        </span>
                      </div>
                      <div className="order-meta-item">
                        <span className="order-meta-label">Totalbeløp</span>
                        <span className="order-meta-value">
                          {paymentAmounts.authorizedAmount.toFixed(2)} NOK
                        </span>
                      </div>
                      <div className="order-meta-item">
                        <span className="order-meta-label">Betalingsstatus</span>
                        <span className={`capture-status ${order.captureStatus?.toLowerCase() || 'pending'}`}>
                          {paymentAmounts.capturedAmount > 0 ? 'INNKREVD' : (order.captureStatus || "AUTORISERT")}
                          {order.capturedAt && (
                            <div className="capture-time">
                              Innkrevd: {new Date(order.capturedAt).toLocaleString('no-NO')}
                            </div>
                          )}
                          {paymentAmounts.refundedAmount > 0 && (
                            <div className="refund-info">
                              Refundert: {paymentAmounts.refundedAmount.toFixed(2)} NOK
                            </div>
                          )}
                        </span>
                      </div>
                    </div>
                    {/* Products List */}
                    <div className="products-section">
                      <h4>Ordrevarer (Refusjonshandlinger)</h4>
                      <div className="products-list">
                        {(order.items || order.products || []).map((item, i) => {
                          const totalItemPrice =
                            item.price && item.quantity
                              ? item.price * item.quantity
                              : item.price || 0;
                          const key = `${order.orderReference}_${item.name}`;
                          const refundedQty = refundedItems[key] || 0;
                          const remainingQty = (item.quantity || 1) - refundedQty;
                          const currentRefundQty = 1;
                          return (
                            <div key={i} className={`product-item ${remainingQty === 0 ? "refunded-item" : ""}`}>
                              <div className="product-info">
                                <span className="product-name" style={{ fontWeight: '600' }}>
                                  {item.quantity}x {item.name}
                                </span>
                                <div className="product-details" style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                                  {item.price} NOK per stk = {totalItemPrice} NOK totalt
                                  {item.category === "klippekort" && item.stampAmounts && (
                                    <span style={{ color: '#7a5af5', marginLeft: '8px' }}>
                                      ({item.stampAmounts} klipp)
                                    </span>
                                  )}
                                  {item.category && (
                                    <span style={{ marginLeft: '8px', textTransform: 'capitalize', background: '#f0f0f0', padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>
                                      {item.category}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="product-actions">
                                {remainingQty === 0 ? (
                                  <span className="refunded-label">Fullt refundert</span>
                                ) : (
                                  <>
                                    <button
                                      className="refund-btn"
                                      onClick={() =>
                                        handleRefund(
                                          order.orderReference,
                                          item,
                                          currentRefundQty,
                                          item.price,
                                          findRefundableTicketDocRef(order, item.name)
                                        )
                                      }
                                      disabled={remainingQty === 0}
                                    >
                                      <FiXCircle />
                                      Refunder 1x
                                    </button>
                                    {refundedQty > 0 && (
                                      <span className="refunded-partial">
                                        Refundert: {refundedQty}x
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Order Actions */}
                    <div className="order-actions">
                      <button
                        className={isOrderCaptured(order) ? "captured-order" : "capture-btn"}
                        onClick={() => handleCapture(order.orderReference, order.totalPrice)}
                        disabled={isOrderCaptured(order) || paymentAmounts.capturedAmount > 0}
                      >
                        <FiCheckCircle />
                        {(isOrderCaptured(order) || paymentAmounts.capturedAmount > 0) ? "Betaling innkrevd" : "Krev inn betaling"}
                      </button>
                      <button
                        className={
                          (refundedOrders[order.orderReference] || paymentAmounts.refundedAmount >= paymentAmounts.capturedAmount)
                            ? "refunded-order"
                            : "refund-btn"
                        }
                        onClick={() =>
                          handleRefund(order.orderReference, null, paymentAmounts.availableToRefund, 1)
                        }
                        disabled={refundedOrders[order.orderReference] || paymentAmounts.availableToRefund <= 0}
                      >
                        <FiXCircle />
                        {(refundedOrders[order.orderReference] || paymentAmounts.availableToRefund <= 0)
                          ? "Ordre fullt refundert"
                          : `Refunder resterende (${paymentAmounts.availableToRefund.toFixed(2)} NOK)`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}
